import { TranslateService } from '@ngx-translate/core';
import { Injectable, Injector } from '@angular/core';
import { map, shareReplay, tap, Observable, of } from 'rxjs';
import { BaseApiService } from './baseapi.service';
import { KeyValueModel } from '../models/keyvalue.model';
import { FilterCondition, FilterCriteria, FilterSelection } from '../models/filter.model';
import { environment } from '@uw-env/environment';
import { UWAlert } from '@uw/shared/models/alert.model';

import { startOfDay } from 'date-fns';

@Injectable({
  providedIn: 'root',
})
export class MetadataService extends BaseApiService {

  private rolesList: KeyValueModel[] | null = null;

  constructor(
    protected injector: Injector,
    protected translateService: TranslateService
  ) {
    super(injector);
  }

  getServiceProvideSSOConfig(): Observable<KeyValueModel[]> {
    const url = `${this.baseApiUrl}/administration/metadata/SPSSOConfig`;
    return this.httpAuthGet(url).pipe(
      map((res) =>
        res && res.data
      )
    );
  }

  getGroupRolesList(): Observable<KeyValueModel[]> {
    const url = `${this.baseApiUrl}/administration/metadata/rolegroup`;
    return this.httpAuthGet(url).pipe(
      map((res) =>
        res && res.data
          ? res.data.map((c: any) => ({
            key: c.key,
            value: c.value,
          }))
          : []
      ),
      tap(data => this.rolesList = data)
    );
  }

  getTRMProfileList(): Observable<KeyValueModel[]> {
    const url = `${this.baseApiUrl}/administration/metadata/TRAProfiles`;
    return this.httpAuthGet(url).pipe(
      map((res) =>
        res && res.data
          ? res.data.map((c: any) => ({
            key: c.key,
            value: c.value,
          }))
          : []
      ),
      tap(data => this.rolesList = data)
    );
  }

  getSUWCatalogList(): Observable<KeyValueModel[]> {
    const url = `${this.baseApiUrl}/administration/metadata/suwcatalog`;
    return this.httpAuthGet(url).pipe(
      map((res) =>
        res && res.data
          ? res.data.map((c: any) => ({
            key: c.key,
            value: `${c.value} (${c.description})`,
          }))
          : []
      ),
      tap(data => this.rolesList = data)
    );
  }

  getTimezones(): Observable<KeyValueModel[]> {
    return this.http.get<{ data: KeyValueModel[] }>(`${this.baseApiUrl}/administration/metadata/timezone`)
      .pipe(
        map(response => response.data)
      )
  }

  getProvinceList(countryCode: string = "CA"): Observable<KeyValueModel[]> {
    const url = `${environment.apiURL}/metadata/provinces/${countryCode}`;
    return this.http.get<{ data: any[] }>(url).pipe(
      map(res => res.data),
      map(dtos => dtos.map(x => ({ key: x.code, value: x.name }))),
      shareReplay(1)
    )
  }

  getCountryList(): Observable<KeyValueModel[]> {
    const url = `${environment.apiURL}/metadata/countries`;
    return this.http.get<{ data: any[] }>(url).pipe(
      map(res => res.data),
      map(dtos => dtos.map(x => ({ key: x.code, value: x.name }))),
      shareReplay(1)
    )
  }


  getTargetTypes(): Observable<KeyValueModel[]> {
    const url = `${this.baseApiUrl}/administration/metadata/target-types`;
    return this.httpAuthGet(url).pipe(
      map((res) =>
        res && res.data
          ? res.data.map((c: any) => ({
            key: c.targetTypeCode,
            value: c.name,
          }))
          : []
      )
    );
  }


  getPrefixTypes(): Observable<KeyValueModel[]> {
    const url = `${this.baseApiUrl}/administration/metadata/prefix`;
    return this.httpAuthGet(url).pipe(
      map((res) =>
        res && res.data
          ? res.data.map((c: any) => ({
            key: c.prefix,
            value: c.prefix,
          }))
          : []
      )
    );
  }

  getNotifications(): Observable<UWAlert[]> {
    const url = `${this.baseApiUrl}/metadata/notifications`;
    return this.httpAuthGet(url).pipe(
      map((res) => {
        const today = startOfDay(new Date());
        const notifications = res && res.data ? res.data.filter((alert: UWAlert) => {
          const startDate = startOfDay(new Date(alert.startDate));
          const endDate = startOfDay(new Date(alert.endDate));
          return today >= startDate && today <= endDate;
        }) : [];

        return notifications;
      }

      )
    );
  }

  getFilterCritera(campaignId: any = null, isEmail: any = null): Observable<FilterCriteria[]> {
    let url = "";

    if (campaignId && !isEmail) {
      url = `${this.baseApiUrl}/administration/metadata/filter/campaign/criteria`;
    }

    if (campaignId && isEmail) {
      url = `${this.baseApiUrl}/administration/metadata/filter/campaign/criteria`;
    }

    if (!campaignId && !isEmail) {
      url = `${this.baseApiUrl}/administration/metadata/filter/profile/criteria`;
    }

    if (!campaignId && isEmail) {
      url = `${this.baseApiUrl}/administration/metadata/filter/profile/criteria`;
    }

    return this.http.get<{ data: FilterCriteria[] }>(url)
      .pipe(
        map(res => res.data),
        map(criterias => {
          if (isEmail) {
            criterias = criterias.filter(c => c.criteriaValue != 'Language');
          }
          return criterias;
        })
      )
  }

  getFilterConditions(criteria: string): Observable<FilterCondition[]> {
    return this.http.get<{ data: FilterCondition[] }>(`${this.baseApiUrl}/administration/metadata/filter/${criteria}/conditions`)
      .pipe(
        map(res => res.data)
      )
  }

  getFilterSelections(criteria: string, campaignId: any = null, isEmail: any = null): Observable<FilterSelection[]> {
    let url = "";

    if (campaignId && !isEmail) {
      url = `${this.baseApiUrl}/administration/metadata/filter/${criteria}/campaign/${campaignId}/selections`;
    }

    if (campaignId && isEmail) {
      url = `${this.baseApiUrl}/administration/metadata/filter/${criteria}/campaign/${campaignId}/selections`;
    }

    if (!campaignId && !isEmail) {
      url = `${this.baseApiUrl}/administration/metadata/filter/${criteria}/selections`;
    }

    if (campaignId && isEmail) {
      url = `${this.baseApiUrl}/administration/metadata/filter/${criteria}/selections`;
    }


    return this.http.get<{ data: FilterSelection[] }>(url)
      .pipe(
        map(res => res.data)
      )
  }


  getLanguages(): Observable<{ value: string, displayName: string }[]> {
    return of([
      { value: "en-CA", displayName: "en_ca" },
      { value: "fr-CA", displayName: "fr_ca" },
      // { value: "en-US", displayName: "en_us" },
    ] as any[]);
  }

}
